import { ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject, combineLatest, Subscription } from 'rxjs';
import { debounceTime, filter, finalize, switchMap, take, takeWhile, tap } from 'rxjs/operators';
import { AnimalModel } from '../../animals/models/animal.model';
import { AnimalWhereaboutsRepository } from '../../animals/states/animal-whereabouts.repository';
import { HelperService } from '../../../shared/services/helper.service';
import { Router } from '@angular/router';
import { SearchService } from '../../../shared/services/search.service';
import { ContactRepository } from '../../contact/states/contact.repository';
import { AnimalSheltersRepository } from '../../../shared/states/animal-shelter.repository';
import { AnimalShelterModel } from '../../../shared/models/animal-shelter.model';
import { AnimalRepository, AnimalSearchFilters } from '../../animals/states/animal.repository';
import { AnimalCategory } from '../../animals/models/animal-category.enum';
import { AnimalType } from '../../animals/models/animal-type.enum';
import { AnimalCoatType } from '../../animals/models/animal-coat-type.enum';
import { AnimalOriginReasonForTransfer } from '../../animals/models/animal-origin-reason-for-transfer.enum';
import { AnimalWhereaboutsType } from '../../animals/models/animal-wherabout-types.enum';
import { AnimalAddPrefillType, AnimalAddService } from '../../animals/services/animal-add.service';

@Component({
  selector: 'app-dashboard-search',
  templateUrl: './dashboard-search.component.html',
  styleUrls: ['./dashboard-search.component.scss'],
  standalone: false,
})
export class DashboardSearchComponent implements OnInit, OnDestroy {
  alive = true;

  @Output() openAddDialog = new EventEmitter<void>();

  searchTerm: string = '';
  searchTrigger = new Subject<void>();
  animalSearchResult: AnimalModel[] = [];
  searching = false;
  searched = false;
  whereabouts: Map<number, any> | undefined;

  showFilters = false;
  filters: AnimalSearchFilters = {};

  first: number | null = null;
  pageSize = 6;
  currentPage = 0;

  categoryOptions: { name: string; value: string }[] = [];
  typeOptions: { name: string; value: string }[] = [];
  coatTypeOptions: { name: string; value: string }[] = [];
  originTypeOptions: { name: string; value: string }[] = [];
  whereaboutTypeOptions: { name: string; value: string }[] = [];
  prefillOptions = [
    { label: 'Transponder', value: 'transponder' as AnimalAddPrefillType },
    { label: 'Buchnummer', value: 'oldBookNumber' as AnimalAddPrefillType },
    { label: 'Name', value: 'name' as AnimalAddPrefillType },
  ];

  prefillType: AnimalAddPrefillType = 'transponder';

  private _subscriptions: Subscription[] = [];


  constructor(
    private readonly _animalRepository: AnimalRepository,
    private readonly _animalWhereaboutsRepository: AnimalWhereaboutsRepository,
    private readonly _helperService: HelperService,
    private readonly _searchService: SearchService,
    private readonly _animalSheltersRepository: AnimalSheltersRepository,
    private readonly _contactRepository: ContactRepository,
    private readonly _router: Router,
    private readonly _cdr: ChangeDetectorRef,
    private readonly _animalAddService: AnimalAddService
  ) {
  }

  ngOnInit(): void {
    this.categoryOptions = this._helperService.getEnumOptions(AnimalCategory, 'animal.category');
    this.typeOptions = this._helperService.getEnumOptions(AnimalType, 'animal.type');
    this.coatTypeOptions = this._helperService.getEnumOptions(AnimalCoatType, 'animal.coatType')
    this.originTypeOptions = this._helperService.getEnumOptions(AnimalOriginReasonForTransfer, 'animalAdd.origin.reasonForTransferSelect', true);
    this.whereaboutTypeOptions = this._helperService.getEnumOptions(AnimalWhereaboutsType, 'animalWhereabout.type', true);

    this.searchChanged();

    const s1 = this._searchService.searchTerm$.pipe(takeWhile(() => this.alive)).subscribe((term) => {
      this.searchTerm = term;
      if (this.canSearch()) {
        this.searchTrigger.next();
      }
    });
    this._subscriptions.push(s1);

    const s2 = this._searchService.searchFilters$.pipe(takeWhile(() => this.alive)).subscribe((filters) => {
      this.filters = { ...(filters || {}) } as AnimalSearchFilters;
      if (this.hasActiveFilters()) {
        this.searchTrigger.next();
      }
    });
    this._subscriptions.push(s2);

    const s3 = this._searchService.showFilters$.pipe(takeWhile(() => this.alive)).subscribe((show) => {
      this.showFilters = !!show;
    });
    this._subscriptions.push(s3);

    const s4 = this._searchService.pagination$.pipe(takeWhile(() => this.alive)).subscribe((p) => {
      if (p) {
        this.first = p.first;
        this.pageSize = p.rows ?? this.pageSize;
        this.currentPage = p.page ?? Math.floor(((p.first ?? 0) as number) / this.pageSize);
      }
    });
    this._subscriptions.push(s4);

    const termSync = this._searchService.searchTerm$.pipe(takeWhile(() => this.alive)).subscribe((term) => {
      this.searchTerm = term;
    });
    this._subscriptions.push(termSync);
  }

  ngOnDestroy(): void {
    this.alive = false;
    this._subscriptions.forEach(s => s.unsubscribe());
    this._subscriptions = [];
  }

  onStartAddAnimal(): void {
    this._animalAddService.setResumeAnimal(null);

    const value = (this.searchTerm ?? '').trim();
    if (value) {
      this._animalAddService.setPrefill({ type: this.prefillType, value });
    } else {
      this._animalAddService.setPrefill(null);
    }

    this._animalAddService.toggleAddDialog(true);
  }

  searchChanged() {
    this.searchTrigger
      .pipe(
        debounceTime(300),
        takeWhile(() => this.alive),
        tap(() => {
          if ((this.searchTerm ?? '').length === 0 && !this.hasActiveFilters()) {
            this.animalSearchResult = [];
            this.searched = false;
          }
        }),
        filter(() => this.canSearch()),
        tap(() => {
          this.searching = true;
          this.searched = false;
        }),
        switchMap(() =>
          combineLatest([
            this._animalSheltersRepository.getAnimalShelters(),
            this._animalRepository.searchAnimalsWithFilters(this.filters, this.searchTerm as string),
          ]).pipe(finalize(() => (this.searching = false)))
        )
      )
      .subscribe({
        next: ([shelters, results]: [AnimalShelterModel[], AnimalModel[]]) => {
          this.animalSearchResult = results.map((animal: AnimalModel) => ({
            ...animal,
            shelter: shelters.find((s: AnimalShelterModel) => s.id === animal.animalShelter?.id) || null,
          }));
          this.loadWhereaboutsForAnimals(this.animalSearchResult);
          this.searched = true;
          this.searching = false;
        },
      });
  }

  onSearchInput() {
    this.resetPagination();
    this._searchService.searchTermSubject.next(this.searchTerm);
    this.searchTrigger.next();
  }

  onFilterChanged() {
    this.resetPagination();
    this._searchService.searchFiltersSubject.next({ ...(this.filters || {}) });
    this.searchTrigger.next();
  }

  toggleFilters() {
    this.showFilters = !this.showFilters;
    this._searchService.showFiltersSubject.next(this.showFilters);
  }

  clearFilters() {
    this.filters = {};
    this.resetPagination();
    this._searchService.searchFiltersSubject.next({} as AnimalSearchFilters);
    this.searchTrigger.next();

    this.toggleFilters();
  }

  applyFilters() {
    this.resetPagination();
    this._searchService.searchFiltersSubject.next({ ...(this.filters || {}) });
    this.searchTrigger.next();
  }

  hasActiveFilters(): boolean {
    const filters = this.filters || {};
    const stringKeys: (keyof AnimalSearchFilters)[] = [
      'competentAuthority',
      'originsContact',
      'whereaboutsContact',
      'category',
      'type',
      'oldBookNumber',
      'petPassportNumber',
      'equinePassportNumber',
      'lifeNumber',
      'transponderNumber',
      'sex',
      'coatType',
      'coatColor',
      'name',
      'birthPlace',
      'originType',
      'whereaboutType',
      'zipCode',
      'medicalFinding',
      'veterinarian',
      'treatmentType',
      'treatmentPreparation',
      'medicalCondition'
    ];
    for (const k of stringKeys) {
      const v = filters[k] as unknown as string | undefined;
      if (v && String(v).trim().length > 0) return true;
    }
    const numericPairs: [number | undefined, number | undefined][] = [
      [filters.weightMin, filters.weightMax],
      [filters.sizeMin, filters.sizeMax],
    ];
    for (const [min, max] of numericPairs) {
      if (typeof min === 'number' || typeof max === 'number') return true;
    }
    const dateKeys: (keyof AnimalSearchFilters)[] = [
      'birthdayStart',
      'birthdayEnd',
      'dateOfReceiptStart',
      'dateOfReceiptEnd',
      'medicalDate',
      'treatmentDueAt',
      'quarantineUntil',
    ];
    for (const k of dateKeys) {
      const v = filters[k] as unknown as string | undefined;
      if (v && String(v).trim().length > 0) return true;
    }

    return false;
  }

  canSearch(): boolean {
    const termOk = this.searchTerm && this.searchTerm.trim().length > 1;

    return !!termOk || this.hasActiveFilters();
  }

  handleBarcodeScanned(event: { field?: string; value: string }) {
    this.searchTerm = event.value;
    this.resetPagination();
    this._searchService.searchTermSubject.next(this.searchTerm);
    this.searchTrigger.next();
  }

  onOpenAddDialogClick() {
    this.openAddDialog.emit();
  }

  loadWhereaboutsForAnimals(animals: AnimalModel[]): void {
    if (!this.whereabouts) {
      this.whereabouts = new Map<number, any>();
    }

    animals.forEach((animal) => {
      this._animalWhereaboutsRepository
        .getByAnimalId(animal.id)
        .pipe(take(1))
        .subscribe((whereabouts: any) => {
          if (Array.isArray(whereabouts) && whereabouts.length > 0) {
            const latestWhereabout = whereabouts.reduce((prev, current) => (prev.id > current.id ? prev : current));

            if (latestWhereabout.contact?.id) {
              this._contactRepository
                .getById(latestWhereabout.contact.id)
                .pipe(take(1))
                .subscribe((contact) => {
                  if (contact.city) {
                    latestWhereabout.location = contact.city;
                  }
                  this.whereabouts!.set(animal.id, latestWhereabout);
                });
            } else {
              this.whereabouts?.set(animal.id, latestWhereabout);
            }
          }
        });
    });
    this._cdr.detectChanges();
  }

  onPageChange(event: { page: number; rows: number; first: number }) {
    this.currentPage = event.page;
    this.pageSize = event.rows;
    this.first = event.first;

    this._searchService.paginationSubject.next({ first: this.first, page: this.currentPage, rows: this.pageSize });

    this.searchTrigger.next();
  }

  private resetPagination() {
    this.currentPage = 0;
    this.first = 0;

    // optional: rows beibehalten (pageSize), oder auch zurücksetzen
    this._searchService.paginationSubject.next({
      first: this.first,
      page: this.currentPage,
      rows: this.pageSize,
    });
  }
}
